import useApiClient from 'hooks/use-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import type { AgentSettingsApi } from '@env0/agent-settings-service/api';
import { useProjectAgentCacheKeys } from 'stores/rq/project-agent-assignments';

const useCacheKeys = () => {
  const orgId = useCurrentOrganizationId();

  const baseKey = [orgId, 'agents'] as const;
  const cacheKeys = {
    all: [...baseKey, 'all'],
    assignments: [...baseKey, 'assignments']
  } as const;

  return cacheKeys;
};

export const useGetAgents = (enabled: boolean) => {
  const apiClient = useApiClient();
  const { all } = useCacheKeys();
  return useQuery({
    queryKey: all,
    queryFn: ({ queryKey: [orgId] }) => apiClient.agents.listAgents(orgId),
    enabled
  });
};

export const useSetDefaultAgent = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { assignments, all } = useCacheKeys();
  const projectAgentKeys = useProjectAgentCacheKeys();

  return useMutation({
    mutationFn: (payload: { agentKey: string; organizationId: string }) => apiClient.agents.setDefaultAgent(payload),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: assignments });
      queryClient.refetchQueries({ queryKey: all });
      queryClient.invalidateQueries({ queryKey: projectAgentKeys.all });
    }
  });
};

export const useGetProjectAgentAssignment = (enabled: boolean) => {
  const apiClient = useApiClient();
  const { assignments } = useCacheKeys();
  return useQuery({
    queryKey: assignments,
    queryFn: ({ queryKey: [orgId] }) => apiClient.agents.getProjectsAssignments(orgId),
    enabled
  });
};

export const useAssignAgentsToProjects = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrganizationId();
  const projectAgentKeys = useProjectAgentCacheKeys();

  return useMutation({
    mutationFn: (payload: AgentSettingsApi.AssignAgents.Request.Body) =>
      apiClient.agents.assignAgentsToProjects(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectAgentKeys.all });
    }
  });
};

export const useCreateAgent = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { all } = useCacheKeys();

  return useMutation({
    mutationFn: (payload: AgentSettingsApi.CreateAgent.Request.Body) => apiClient.agents.createAgent(payload),
    onSuccess: newAgent => {
      queryClient.setQueryData(all, (agents?: AgentSettingsApi.ListAgents.Response) =>
        agents ? [...agents, newAgent] : [newAgent]
      );
      queryClient.refetchQueries({ queryKey: all });
    }
  });
};

export const useUpdateAgent = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { all } = useCacheKeys();
  const projectAgentKeys = useProjectAgentCacheKeys();

  return useMutation({
    mutationFn: ({ agentKey, data }: { agentKey: string; data: AgentSettingsApi.UpdateAgent.Request.Body }) =>
      apiClient.agents.updateAgent(agentKey, data),
    onSuccess: updatedAgent => {
      queryClient.setQueryData(all, (agents?: AgentSettingsApi.ListAgents.Response) =>
        agents?.map(agent => (agent.agentKey === updatedAgent.agentKey ? updatedAgent : agent))
      );
      queryClient.refetchQueries({ queryKey: all });
      queryClient.invalidateQueries({ queryKey: projectAgentKeys.all });
    }
  });
};

export const useDeleteAgent = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { all } = useCacheKeys();
  const projectAgentKeys = useProjectAgentCacheKeys();

  return useMutation({
    mutationFn: (agentKey: string) => apiClient.agents.deleteAgent(agentKey),
    onSuccess: (_data, agentKey) => {
      queryClient.setQueryData(all, (agents?: AgentSettingsApi.ListAgents.Response) =>
        agents?.filter(agent => agent.agentKey !== agentKey)
      );
      queryClient.refetchQueries({ queryKey: all });
      queryClient.invalidateQueries({ queryKey: projectAgentKeys.all });
    }
  });
};

export const useGetAgentSecrets = (agentKey: string, enabled: boolean) => {
  const apiClient = useApiClient();
  const orgId = useCurrentOrganizationId();

  return useQuery({
    queryKey: [orgId, 'agents', agentKey, 'secrets'],
    queryFn: () => apiClient.agents.getAgentSecrets(agentKey),
    enabled
  });
};

export const useRevokeAgentSecret = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrganizationId();

  return useMutation({
    mutationFn: ({ agentKey, secretId }: { agentKey: string; secretId: string }) =>
      apiClient.agents.revokeAgentSecret(agentKey, secretId),
    onSuccess: (_data, { agentKey }) => {
      queryClient.refetchQueries({ queryKey: [orgId, 'agents', agentKey, 'secrets'] });
    }
  });
};

export const useCreateAgentSecret = () => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const orgId = useCurrentOrganizationId();

  return useMutation({
    mutationFn: ({ agentKey, description }: { agentKey: string; description?: string }) =>
      apiClient.agents.createAgentSecret(agentKey, { description }),
    onSuccess: (_data, { agentKey }) => {
      queryClient.refetchQueries({ queryKey: [orgId, 'agents', agentKey, 'secrets'] });
    }
  });
};
